import dayjs from "dayjs";
import { useEffect, useMemo } from "react";
import { WorkStatus } from "tm-shared/types";

import { reminderDelayTimeType, reminderDelayValue } from "@/config";
import { useAppDispatch, useAppSelector, useStableRef } from "@/hooks";
import { setCheckoutReminder, setTaskReminder } from "@/store/slices/offline";
import { selectUncompletedTasks } from "@/store/slices/progress";
import { addModalToQueue } from "@/store/slices/ui";
import { ModalType } from "@/types/modals";

const HOUR_IN_MS = 60 * 60 * 1000;

const calcDelayMs = (lastIso: string | null) => {
    const now = dayjs();

    if (!lastIso) return 0;

    const elapsed = now.diff(dayjs(lastIso), reminderDelayTimeType);
    if (elapsed >= reminderDelayValue) return 0;

    return (reminderDelayValue - elapsed) * HOUR_IN_MS;
};

export const useReminder = () => {
    const dispatch = useAppDispatch();

    const { checkout: checkoutTimestamp, task: taskTimestamp } = useAppSelector(
        (s) => s.offline.reminderTimestamps,
    );
    const checkoutList = useAppSelector((s) => s.work.checkoutList);
    const workStatus = useAppSelector((s) => s.work.workStatus);
    const uncompletedTasks = useAppSelector(selectUncompletedTasks);

    const isStartedWork = workStatus === WorkStatus.StartWork;
    const hasCheckouts = useMemo(
        () => Object.values(checkoutList).length > 0,
        [checkoutList],
    );

    const taskTsRef = useStableRef(taskTimestamp ?? null);
    const checkoutTsRef = useStableRef(checkoutTimestamp ?? null);

    useEffect(() => {
        if (!isStartedWork) return;

        if (!hasCheckouts) {
            const iso = dayjs().toISOString();
            checkoutTsRef.current = iso;
            dispatch(setCheckoutReminder(iso));
            return;
        }

        const delayMs = calcDelayMs(checkoutTsRef.current);

        const timer = window.setTimeout(() => {
            dispatch(
                addModalToQueue({
                    type: ModalType.UncompletedCheckouts,
                    id: ModalType.UncompletedCheckouts,
                }),
            );

            const iso = dayjs().toISOString();
            checkoutTsRef.current = iso;
            dispatch(setCheckoutReminder(iso));
        }, delayMs);

        return () => window.clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStartedWork, hasCheckouts, dispatch]);

    useEffect(() => {
        if (!isStartedWork) return;

        if (!uncompletedTasks.length) {
            const iso = dayjs().toISOString();
            taskTsRef.current = iso;
            dispatch(setTaskReminder(iso));
            return;
        }

        const delayMs = calcDelayMs(taskTsRef.current);

        const timer = window.setTimeout(() => {
            dispatch(
                addModalToQueue({
                    type: ModalType.UncompletedTasks,
                    id: ModalType.UncompletedTasks,
                }),
            );

            const iso = dayjs().toISOString();
            taskTsRef.current = iso;
            dispatch(setTaskReminder(iso));
        }, delayMs);

        return () => window.clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStartedWork, uncompletedTasks.length, dispatch]);
};
